import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '../../hooks/useSeo';
import { BlogLayout, Chip, MetaRow } from './BlogLayout';
import { Emphasis } from './Emphasis';
import { getAuthor } from './authors';
import { formatDate } from './format';
import { CATEGORIES, POSTS } from './registry';
import type { BlogPost } from './types';

const BLOG_DESCRIPTION =
  'Field notes on subscription revenue: pricing, retention, dunning, and the metrics that actually move ARR. Written for the operators running the engine.';

function PostCard({ post }: { post: BlogPost }) {
  return (
    <Link className="rv-card" to={`/blog/${post.slug}`}>
      <Chip label={post.category} />
      <h3>
        <Emphasis text={post.title} />
      </h3>
      <p className="excerpt">{post.excerpt}</p>
      <MetaRow
        author={getAuthor(post.author).name}
        readTime={post.readTime}
        date={formatDate(post.date)}
      />
    </Link>
  );
}

export function BlogHome() {
  const [activeFilter, setActiveFilter] = useState('All');

  useSeo({
    title: 'Blog — Revvly',
    description: BLOG_DESCRIPTION,
    path: '/blog',
    type: 'website',
  });

  const filtered = useMemo(
    () => (activeFilter === 'All' ? POSTS : POSTS.filter((p) => p.category === activeFilter)),
    [activeFilter],
  );

  // The editorial layout (one lead story + three secondary + a grid) only makes
  // sense across the full set. When a filter is applied, fall back to a plain grid.
  const showFeatured = activeFilter === 'All' && filtered.length > 0;
  const [lead, ...rest] = filtered;
  const secondary = showFeatured ? rest.slice(0, 3) : [];
  const grid = showFeatured ? rest.slice(3) : filtered;

  return (
    <BlogLayout>
      {/* Hero */}
      <header className="rv-hero">
        <p className="rv-eyebrow">Revenue intelligence</p>
        <h1>
          Subscription growth, without the <em>spreadsheets</em>.
        </h1>
        <p>
          Field notes on subscription revenue: pricing, retention, dunning, and the metrics that
          actually move ARR. Written for the operators running the engine.
        </p>
      </header>

      {/* Filter pill bar */}
      <div className="rv-filters">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            className={`rv-pill${activeFilter === cat ? ' on' : ''}`}
            onClick={() => setActiveFilter(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Featured section (full list only) */}
      {showFeatured && (
        <section className="rv-featured">
          <Link className="rv-feature-main" to={`/blog/${lead.slug}`}>
            <Chip label={lead.category} />
            <h2>
              <Emphasis text={lead.title} />
            </h2>
            <p className="excerpt">{lead.excerpt}</p>
            <MetaRow
              author={getAuthor(lead.author).name}
              readTime={lead.readTime}
              date={formatDate(lead.date)}
            />
            <span className="rv-readmore">
              Read article <span className="chev">›</span>
            </span>
          </Link>
          <div className="rv-feature-side">
            {secondary.map((post) => (
              <Link key={post.slug} className="rv-side-item" to={`/blog/${post.slug}`}>
                <Chip label={post.category} />
                <h3>
                  <Emphasis text={post.title} />
                </h3>
                <MetaRow
                  author={getAuthor(post.author).name}
                  readTime={post.readTime}
                  date={formatDate(post.date)}
                />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Article grid */}
      {grid.length > 0 && (
        <section className="rv-grid">
          {grid.map((post) => (
            <PostCard key={post.slug} post={post} />
          ))}
        </section>
      )}

      {/* Newsletter — HIDDEN: the signup form is non-functional (no submit
          handler / email provider wired up). Left in place for whoever plugs it
          in properly (e.g. via an agent): connect the form to the newsletter
          backend, then un-hide this section. */}
      {/*
      <section className="rv-newsletter">
        <div>
          <h4>The revenue operators' brief.</h4>
          <p>One considered read on subscription revenue, every other Tuesday. No noise.</p>
        </div>
        <form onSubmit={(e) => e.preventDefault()}>
          <input
            className="rv-input"
            type="email"
            placeholder="you@company.com"
            aria-label="Email address"
          />
          <button className="rv-btn-accent" type="submit">
            Subscribe
          </button>
        </form>
      </section>
      */}
    </BlogLayout>
  );
}
